import { ReactNode, useMemo, useState } from 'react'
import {
	Label,
	ListBox,
	ListBoxItem,
	Popover,
	SelectProps,
	Selection,
	DialogTrigger,
	Dialog,
} from 'react-aria-components'
import { useToggle } from '../use-toggle'
import { Button } from './button'
import { getTextValue, Option, Options } from './select'

const listBoxItemBase = 'cursor-pointer px-2 py-1 outline-0 rounded group'

function getLabel(option?: Option) {
	if (!option) return 'Smazaná hodnota'
	return option.label ?? option.value
}

export type MultiSelectProps<T extends object = object> = Omit<
	SelectProps<T>,
	'onSelectionChange'
> & {
	label?: ReactNode
	options: Options
	selectedKeys: Array<string | number>
	onSelectionChange: (selectedKeys: Array<string | number>) => void
}

export function MultiSelect<T extends object>({
	name,
	label,
	options,
	selectedKeys,
	onSelectionChange,
}: MultiSelectProps<T>) {
	const [isOpen, toggle] = useToggle()
	const [triggerRef, setTriggerRef] = useState<HTMLButtonElement | null>(null)
	const currentValue = useMemo(() => {
		if (selectedKeys.length === 0) {
			return 'Vyberte'
		}
		if (selectedKeys.length === 1) {
			return getLabel(options.find(option => option.value === selectedKeys[0]))
		}
		return 'Více hodnot'
	}, [selectedKeys, options])

	// TODO: ignores window resize
	const width = useMemo(() => {
		return triggerRef?.getBoundingClientRect().width
	}, [triggerRef])

	return (
		<div>
			{selectedKeys.map(key => (
				<input key={key} type="hidden" name={`${name}[]`} value={key} />
			))}
			<Label className="flex font-normal leading-tight text-xs mb-1">{label}</Label>
			<DialogTrigger>
				<div className="relative">
					<Button
						variant="INVISIBLE"
						size="NONE"
						onPress={toggle}
						span="FULL"
						innerRef={setTriggerRef}
						className="!border-gray-400"
					>
						<span className="flex justify-between items-center w-full gap-2">
							<span className="w-full text-left pr-6">
								{currentValue as ReactNode}
							</span>
							<span>▼</span>
						</span>
					</Button>
					{selectedKeys.length ? (
						<Button
							size="NONE"
							variant="TEXT"
							className="p-1 text-black leading-none absolute right-6 top-1/2 -translate-y-1/2"
							aria-label="Odebrat"
							onPress={() => {
								onSelectionChange([])
							}}
						>
							╳
						</Button>
					) : null}
				</div>
				<Popover
					isOpen={isOpen}
					onOpenChange={toggle}
					className="bg-white rounded border border-gray-200 shadow-xl shadow-gray-200 overflow-auto w-[--trigger-width]"
					placement="bottom start"
					offset={0}
					style={{ minWidth: width }}
				>
					<MultiSelectPopover
						onSelectionChange={onSelectionChange}
						options={options}
						selectedKeys={selectedKeys}
						toggle={toggle}
					/>
				</Popover>
			</DialogTrigger>
		</div>
	)
}

function MultiSelectPopover({
	onSelectionChange,
	options,
	selectedKeys,
	toggle,
}: Pick<MultiSelectProps, 'selectedKeys' | 'onSelectionChange' | 'options'> & {
	toggle: VoidFunction
}) {
	const [selected, setSelected] = useState<Selection>(() => new Set(selectedKeys))

	const onSubmit = () => {
		toggle()
		onSelectionChange(Array.from(selected))
	}

	return (
		<Dialog className="outline-0 flex flex-col max-h-[inherit]">
			<ListBox
				className="flex flex-col gap-[1px] p-2 flex-auto overflow-auto"
				selectionMode="multiple"
				selectedKeys={selected}
				onSelectionChange={setSelected}
				aria-label="Možnosti"
			>
				{options.map(option => {
					return (
						<ListBoxItem
							className={({ isHovered, isSelected, isPressed, isFocusVisible }) => {
								return `${listBoxItemBase} ${isPressed ? 'bg-blue-300' : isSelected && (isHovered || isFocusVisible) ? 'bg-blue-100' : isHovered || isFocusVisible ? 'bg-blue-50' : isSelected ? 'bg-blue-200' : ''}`
							}}
							textValue={getTextValue(option)}
							key={option.value}
							id={option.value}
						>
							{getLabel(option)}
						</ListBoxItem>
					)
				})}
			</ListBox>
			<div className="border-t border-gray-200 p-2 flex justify-center items-center">
				<Button variant="PRIMARY" onPress={onSubmit}>
					Potvrdit
				</Button>
			</div>
		</Dialog>
	)
}
